import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Child } from "../api/child";
import { useGlobalStore } from "../api/globalStores";
import Avatar from "../Common/Avatars/Avatar";
import MobileVersionNotAvailable from "../Common/Utility/MobileVersionNotAvailable";
import { definitions } from "../api/supabase/client";

export default observer(ChildSelection);

function ChildSelection() {
  const navigate = useNavigate();
  const globalStores = useGlobalStore();

  const [children, setChildren] = useState<Child[]>([]);

  useEffect(() => {
    const run = async () => {
      const newChildren = await globalStores.parentStore.getChildren();
      setChildren(newChildren);
    };

    run();
  }, [globalStores.parentStore.parentAccount.id]);

  const [paymentDetails, setPaymentDetails] = useState<
    definitions["PaymentStatus"][]
  >([]);

  useEffect(() => {
    const run = async () => {
      const newPaymentDetails =
        await globalStores.parentStore.getPaymentDetails();
      setPaymentDetails(newPaymentDetails);
    };

    run();
  }, [children]);

  const payingChildIDs = paymentDetails.map(
    (paymentDetail) => paymentDetail.account
  );

  return (
    <div className="inline-flex h-full w-full">
      <div className="flex h-full w-full bg-center flex-col items-center justify-start gap-2.5 overflow-clip bg-[url(https://uortjlczjmucmpaqqhqm.supabase.co/storage/v1/object/public/firejet-converted-images/6554/8ed16c7d87439ffb6cfe6d05fb8a5e28c640b204.webp)] bg-cover px-6 pt-16 pb-2.5">
        <div className="flex flex-col w-full sm:w-auto items-center justify-start gap-6 rounded-3xl bg-[rgba(255,254,252,1)] px-6 py-12 drop-shadow-lg overflow-y-scroll overflow-x-clip">
          <div className="hidden smallvp:flex flex-col gap-6">
            <p className="text-center font-['Poppins'] text-xl font-medium leading-[1.4] text-[rgba(109,109,109,1)]">
              Log In for:
            </p>
            <div className="flex-wrap justify-center items-center">
              {children.map((child) => (
                <AccountCard
                  key={child.childAccount.id}
                  child={child}
                  isPaid={payingChildIDs.includes(
                    child.childAccount.id ?? null
                  )}
                />
              ))}
            </div>
          </div>
          <div className="flex smallvp:hidden">
            <MobileVersionNotAvailable />
          </div>

          <p className="h-[39px] w-[402px] text-center font-['Poppins'] text-2xl font-semibold leading-[1.5] text-[rgba(109,109,109,1)]">
            or
          </p>
          <div
            className="flex w-full items-end justify-center gap-2.5 p-1.5 cursor-pointer"
            onClick={() => {
              navigate("/parent/dashboard");
            }}
          >
            <p className="text-right font-['Poppins'] text-xl font-medium leading-[1.4] text-[rgba(109,109,109,1)]">
              Continue as Parent
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

interface AccountCardInterface {
  child: Child;
  isPaid: boolean;
}

const AccountCard = observer((props: AccountCardInterface) => {
  const globalStores = useGlobalStore();
  const navigate = useNavigate();

  const _handleLogin = async () => {
    // if (!props.isPaid) {
    //   throw new Error("Please complete payment to use child account!");
    // }
    if (!props.child.childAccount.id)
      throw new Error("Child ID not defined for Login");
    await globalStores.childStore.init(props.child.childAccount.id);
    navigate("/student/login");
  };

  const handleLogin = () => {
    toast.promise(_handleLogin(), {
      success: "Login Screen Loaded",
      loading: "Loading Login Screen...",
      error: (e) => e.message,
    });
  };

  return (
    <div className="inline-flex cursor-pointer" onClick={handleLogin}>
      <div className="flex flex-col items-center justify-start gap-[18px] overflow-clip rounded-xl p-3">
        <div className="h-20 w-20">
          <Avatar icon={Child.getIcon(props.child)} active={false} large />
        </div>
        <div className="gap-1">
          <p className="h-5 w-[110px] text-center font-['Poppins'] text-sm font-medium leading-[1.4] text-[rgba(50,50,50,1)]">
            {props.child.userDetail.firstName}
          </p>
        </div>
      </div>
    </div>
  );
});

function AnimalAvatar(props: AnimalAvatarInterface) {
  return (
    <>
      {props.firejetVariant === "VARIANT=GECKO_TYPE0" && (
        <div className="inline-flex">
          <div className="flex h-20 w-20 items-start justify-start gap-2.5 overflow-clip rounded-[66px] bg-[rgba(242,245,252,1)] p-1 drop-shadow-lg">
            <div className="flex-1 bg-[url(https://uortjlczjmucmpaqqhqm.supabase.co/storage/v1/object/public/firejet-converted-images/6554/d4893c1d26ce2a48d7dbbcd6a85251461ff4becb.webp)] bg-cover" />
          </div>
        </div>
      )}
      {props.firejetVariant === "VARIANT=SAURPOD_TYPE1" && (
        <div className="inline-flex">
          <div className="flex h-20 w-20 items-start justify-start gap-2.5 overflow-clip rounded-[99px] bg-[rgba(242,245,252,1)] p-1 drop-shadow-lg">
            <div className="flex-1 bg-[url(https://uortjlczjmucmpaqqhqm.supabase.co/storage/v1/object/public/firejet-converted-images/6554/f36703647b9924650c29a3eda9a7c42174e60822.webp)] bg-cover" />
          </div>
        </div>
      )}
      {props.firejetVariant === "VARIANT=LION_TYPE2" && (
        <div className="inline-flex">
          <div className="flex h-20 w-20 items-start justify-start gap-2.5 overflow-clip rounded-[99px] bg-[rgba(242,245,252,1)] p-1 drop-shadow-lg">
            <div className="flex-1 bg-[url(https://uortjlczjmucmpaqqhqm.supabase.co/storage/v1/object/public/firejet-converted-images/6554/6fa392259988c059d6039bbba12f34f5ea4d7ca9.webp)] bg-cover" />
          </div>
        </div>
      )}
      {props.firejetVariant === "VARIANT=PENGUIN_TYPE3" && (
        <div className="inline-flex">
          <div className="flex h-20 w-20 items-center justify-center gap-2.5 overflow-clip rounded-[99px] bg-[rgba(242,245,252,1)] p-1 drop-shadow-lg">
            <div className="flex-1 bg-[url(https://uortjlczjmucmpaqqhqm.supabase.co/storage/v1/object/public/firejet-converted-images/6554/1a2ffdb0bc1e278edf2aed61aee347ab9d2fc0f4.webp)] bg-cover" />
          </div>
        </div>
      )}
      {props.firejetVariant === "VARIANT=DOLPHIN_TYPE4" && (
        <div className="inline-flex">
          <div className="flex h-20 w-20 items-center justify-center gap-2.5 overflow-clip rounded-[99px] bg-[rgba(242,245,252,1)] p-1 opacity-60 drop-shadow-lg">
            <div className="relative flex-1">
              <div className="absolute inset-0 bg-[url(https://uortjlczjmucmpaqqhqm.supabase.co/storage/v1/object/public/firejet-converted-images/6554/2cb4c3d4e376496209b1fa46bb44b84cd32bdf5f.webp)] bg-cover" />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

AnimalAvatar.defaultProps = {
  firejetVariant: "VARIANT=GECKO_TYPE0",
};

interface AnimalAvatarInterface {
  firejetVariant:
    | "VARIANT=GECKO_TYPE0"
    | "VARIANT=SAURPOD_TYPE1"
    | "VARIANT=LION_TYPE2"
    | "VARIANT=PENGUIN_TYPE3"
    | "VARIANT=DOLPHIN_TYPE4";
}
